import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { setTokenGetter, setLoginRedirect } from '@/api/client';
import { logger } from '@/lib/logger';
import { useProspectClaim } from '@/hooks/useProspectClaim';

export default function TokenConfigProvider({ children }: { children: React.ReactNode }) {
  const { getAccessTokenSilently, loginWithRedirect, isAuthenticated, isLoading } = useAuth0();

  // Card 6a333d48 / F2 — Merge-on-login: cuando el usuario se loguea, reclamamos
  // el prospect anónimo (matching por conversationId persistido en localStorage)
  // para no perder el contexto del wizard / diagnóstico previo. Vive acá porque
  // este componente está debajo de Auth0Provider y tiene acceso al chatUIStore.
  useProspectClaim();

  // Mantener refs sincronizadas con el estado actual de Auth0. Permite que el
  // tokenGetter (registrado una sola vez) lea el último valor sin re-registrarse
  // en cada cambio. Ver bug Trello C4WFmiEo: en rutas públicas, el closure
  // congelaba isAuthenticated=false y luego, tras login, no se actualizaba.
  const isAuthenticatedRef = React.useRef(isAuthenticated);
  const isLoadingRef = React.useRef(isLoading);

  React.useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
    isLoadingRef.current = isLoading;
  }, [isAuthenticated, isLoading]);

  // Configurar el token getter (solo una vez)
  const tokenGetterSetupRef = React.useRef(false);

  React.useEffect(() => {
    if (tokenGetterSetupRef.current) return;
    tokenGetterSetupRef.current = true;

    const tokenGetter = async () => {
      // Guard: si Auth0 aún está cargando o el usuario NO está autenticado,
      // no intentar refresh. Sin este guard, getAccessTokenSilently() arroja
      // "Missing Refresh Token" cada vez que un usuario anónimo carga la app
      // (ruido en consola en producción/staging — ver Trello C4WFmiEo).
      if (isLoadingRef.current) {
        logger.debug('[TOKEN_CONFIG] Auth0 still loading, returning null');
        return null;
      }
      if (!isAuthenticatedRef.current) {
        logger.debug('[TOKEN_CONFIG] User not authenticated, returning null (expected for public routes)');
        return null;
      }

      try {
        const token = await getAccessTokenSilently({
          audience: 'https://auth0.inbestai.com',
          scope: 'openid profile email read:companies create:companies update:companies delete:companies read:all_companies read:projects create:projects update:projects delete:projects read:properties create:properties update:properties delete:properties offline_access'
        });
        if (import.meta.env.DEV) logger.debug('[TOKEN_CONFIG] Token obtained, length:', token?.length ?? 0);
        return token;
      } catch (error: unknown) {
        const errorObj = error as { error?: string; message?: string };
        // Casos esperados: sesión expirada o sin refresh token disponible.
        // No los logueamos como error para no contaminar consola/Sentry.
        if (errorObj?.error === 'login_required' ||
            errorObj?.message?.includes('Missing Refresh Token') ||
            errorObj?.message?.includes('consent_required')) {
          logger.debug('[TOKEN_CONFIG] No active session, returning null:', errorObj?.message);
          return null;
        }
        // Errores genuinos (red, config rota) sí los queremos visibles como warning.
        logger.warn('[TOKEN_CONFIG] Unexpected error getting token:', error instanceof Error ? error.message : 'Unknown error');
        return null;
      }
    };
    setTokenGetter(tokenGetter);
  }, [getAccessTokenSilently]);

  // Configurar el login redirect (solo una vez)
  const redirectSetupRef = React.useRef(false);

  React.useEffect(() => {
    if (redirectSetupRef.current) return;
    redirectSetupRef.current = true;

    const redirectFn = () => {
      loginWithRedirect({
        prompt: 'consent',
        appState: {
          returnTo: window.location.pathname
        }
      });
    };
    setLoginRedirect(redirectFn);
  }, [loginWithRedirect]);

  return <>{children}</>;
}
